import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { AppShell } from '../design-system/shell/app-shell';
import { NavIdentity } from '../design-system/shell/nav';
import { PageHeader } from '../design-system/shell/page-header';
import { ActionButton } from '../design-system/concept/action-button';
import { CoachCard } from '../design-system/concept/coach-card';
import { Eyebrow } from '../design-system/concept/eyebrow';
import { KpiTile } from '../design-system/overview/kpi-tile';
import { AttentionRow, type AttentionItem } from '../design-system/overview/attention-row';
import { ActivityPanel } from '../design-system/overview/activity-panel';
import { NewScanSheet, type NewScanClient } from '../design-system/overview/new-scan-sheet';
import { SearchIcon } from '../design-system/shell/icons';
import { COACH_NAV } from '../components/dashboard/nav-entries';
import { SignInPanel } from '../design-system/shell/sign-in-panel';
import { LoadErrorCard } from '../design-system/concept/load-error-card';

import { useAuth } from '../context/AuthContext';
import {
  AuthProvider,
  getProfiles,
  getMeasurements,
  getProfileSummaries,
  initSession,
  sendLinkEmail,
  type ProfileSummary,
} from '../lib/http-service';
import { sessionPath } from '../lib/scan/sessionPath';
import { greeting, waitingLine } from '../lib/roster/greeting';
import { useSystemTheme } from '../lib/theme/useSystemTheme';
import { normalizeToHeight } from '../lib/dashboard/data';
import { describeLoadError, type LoadErrorCopy } from '../lib/load-error';
import { ACTIVITY_DAYS, buildOverview, type RosterRow } from '../lib/roster/overview';
import {
  actionFor,
  detailFor,
  headlineFor,
  rowsForView,
  viewTitle,
  type OverviewView,
} from '../lib/roster/overviewView';
import type { ClientData, MeasureData } from '../lib/interfaces';
import { hasFeature, noun } from '../lib/features';

// Retail sets no targets and reads no body fat; its overview is recency and
// review only.
const GOAL = hasFeature('goal');
const BODY_FAT = hasFeature('bodyFat');

/**
 * Run `jobs` a few at a time.
 *
 * Measurements are only reachable one profile at a time — there is no roster
 * summary endpoint — so a coach with 42 clients costs 42 requests. Unbounded
 * they all leave at once and the browser queues them anyway; six at a time
 * keeps the first rows arriving quickly and leaves room for the meshes the
 * client page will want next.
 */
async function pooled<T>(jobs: (() => Promise<T>)[], width = 6): Promise<T[]> {
  const out: T[] = new Array(jobs.length);
  let next = 0;
  await Promise.all(
    Array.from({ length: Math.min(width, jobs.length) }, async () => {
      while (next < jobs.length) {
        const i = next++;
        out[i] = await jobs[i]();
      }
    }),
  );
  return out;
}

/**
 * The coach overview — "Vem behöver mig nu?".
 *
 * Built to the reference deck's slide 4 information architecture, in this
 * app's design language rather than the deck's. Two of its visual choices are
 * deliberately not carried across: the dark navy rail, and the four
 * colour-coded tiles, which are the traffic light the concept drops.
 *
 * Two of the deck's right-hand rows are also absent — "Uppföljningar" and
 * "Påminnelser" count events that do not exist in the data model. Inventing
 * them would put two fabricated numbers on the screen a coach is meant to
 * trust in ten seconds.
 */
const CoachOverview: React.FC = () => {
  const history = useHistory();
  const { isAuthenticated, isLoading, getAccessTokenSilently, signIn, user } =
    useAuth();
  const [auth] = useState(() => new AuthProvider(getAccessTokenSilently));
  useSystemTheme();

  const [profiles, setProfiles] = useState<ClientData[]>([]);
  const [measurements, setMeasurements] = useState<Map<string, MeasureData[]>>(new Map());
  const [scanCounts, setScanCounts] = useState<Map<string, number>>(new Map());
  const [profilesLoaded, setProfilesLoaded] = useState(false);
  const [scansLoaded, setScansLoaded] = useState(false);
  /** True when the roster came from the summary endpoint rather than N calls. */
  const [summarised, setSummarised] = useState(false);
  // The roster request failed. With the API down this page used to show four
  // zeroes and "Loading clients…" for ever, which reads as "no clients".
  const [loadError, setLoadError] = useState<LoadErrorCopy | null>(null);
  // Bumped by Try again; the effect below refetches on it.
  const [attempt, setAttempt] = useState(0);
  // Which tile the list is showing, and what is typed in the search.
  const [view, setView] = useState<OverviewView>('attention');
  const [query, setQuery] = useState('');
  // The New scan sheet, and the client a row opened it for, if one did.
  const [scanSheet, setScanSheet] = useState<{ open: boolean; startWith?: string }>({ open: false });

  /**
   * One scan summary, shaped as the measurement the builder expects.
   *
   * The summary carries a boolean for "has a coach been through this", while
   * the builder asks whether confirmedMeasurements is non-empty. A single
   * marker field answers that without pretending to carry readings nobody
   * sent.
   */
  const asMeasure = (
    profileID: string,
    scan: ProfileSummary['first'],
    height?: number,
  ): MeasureData | null => {
    if (!scan) return null;
    const raw = {
      ID: `${profileID}-${scan.createdAt}`,
      sessionID: `${profileID}-${scan.createdAt}`,
      profileID,
      client: profileID,
      createdAt: scan.createdAt,
      measurements: scan.measurements ?? {},
      ...(scan.confirmed ? { confirmedMeasurements: { confirmed: 1 } } : {}),
    } as unknown as MeasureData;
    // Millimetres on the wire, as everywhere else.
    return normalizeToHeight(raw, height);
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    let live = true;

    (async () => {
      try {
      // One request for the whole roster where the API offers it. Falls back
      // to a profile list plus one call per client, which is what this did
      // before the endpoint existed and still does against an older deploy.
      const summaries = await getProfileSummaries(auth);
      if (!live) return;

      if (summaries) {
        setProfiles(
          summaries.map(
            (s) =>
              ({
                ID: s.ID,
                firstName: s.firstName,
                lastName: s.lastName,
                email: s.email,
                gender: s.gender,
                age: s.age,
                height: s.height,
                createdAt: s.createdAt,
                lastMeasure: s.lastMeasure,
                ...(s.targetBodyFat !== null
                  ? { target: { measurements: { body_fat: s.targetBodyFat } } }
                  : {}),
              }) as unknown as ClientData,
          ),
        );
        setMeasurements(
          new Map(
            summaries.map((s) => [
              s.ID,
              [asMeasure(s.ID, s.first, s.height), asMeasure(s.ID, s.latest, s.height)]
                .filter((m): m is MeasureData => m !== null)
                // One scan means first and latest are the same record.
                .filter((m, i, all) => all.findIndex((x) => x.ID === m.ID) === i),
            ]),
          ),
        );
        setScanCounts(new Map(summaries.map((s) => [s.ID, s.scanCount])));
        setSummarised(true);
        setProfilesLoaded(true);
        setScansLoaded(true);
        return;
      }

      const list = await getProfiles(auth);
      if (!live) return;
      setProfiles(list);
      setProfilesLoaded(true);

      // Second pass. Everything that needs only the profile — the queue's
      // ordering, three of the four tiles — is already on screen by now.
      const pairs = await pooled(
        list.map((p) => async () => {
          try {
            // Normalised exactly as ClientDataHolder does. The API serves
            // millimetres; without this the tape values are ten times too
            // large and the body fat computed from them is nonsense.
            const raw = await getMeasurements(p.ID, auth);
            return [p.ID, raw.map((m) => normalizeToHeight(m, p.height))] as const;
          } catch {
            return [p.ID, [] as MeasureData[]] as const;
          }
        }),
      );
      if (!live) return;
      setMeasurements(new Map(pairs));
      setScanCounts(new Map(pairs.map(([id, ms]) => [id, ms.length])));
      setScansLoaded(true);
      } catch (e) {
        if (live) setLoadError(describeLoadError(e));
      }
    })();

    return () => {
      live = false;
    };
  }, [isAuthenticated, attempt]);

  const retry = () => {
    setLoadError(null);
    setProfilesLoaded(false);
    setScansLoaded(false);
    setAttempt((n) => n + 1);
  };

  const { kpis, queue, rows } = useMemo(
    () => buildOverview(profiles, measurements, new Date(), scanCounts, { goals: GOAL }),
    [profiles, measurements, scanCounts],
  );

  const shown = rowsForView(rows, queue, view, query);
  const items: AttentionItem[] = shown.map((r) => ({
    id: r.id,
    name: r.name,
    initials: r.initials,
    headline: headlineFor(r),
    detail: detailFor(r, BODY_FAT),
    actionLabel: actionFor(r),
    primary: r.reason === 'goal-reached',
  }));
  const filtered = view !== 'attention' || query.trim() !== '';
  // A tile pressed twice goes back to the queue, as a toggle would.
  const pick = (v: OverviewView) => setView((cur) => (cur === v ? 'attention' : v));

  // Signed out, this rendered an empty roster and four zeroes — which reads as
  // "you have no clients" rather than "you are not signed in".
  if (!isAuthenticated) {
    return (
      <SignInPanel
        state={isLoading ? 'checking' : 'signed-out'}
        subject="clients"
        onSignIn={() => signIn()}
      />
    );
  }

  const today = new Date().toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

  const open = (id: string) => history.push(`/dashboard/${id}`);
  const act = (r: RosterRow) => {
    // Gone quiet or never started: the row's button is Send scan link, and it
    // opens the sheet on that client rather than their page.
    if (r.reason === 'overdue' || r.reason === 'never-scanned') {
      setScanSheet({ open: true, startWith: r.id });
      return;
    }
    history.push(
      r.reason === 'scan-to-review'
        ? `/dashboard/${r.id}?show=settings`
        : r.reason === 'goal-reached'
          ? `/dashboard/${r.id}?show=goal`
          : `/dashboard/${r.id}`,
    );
  };

  const profileById = new Map(profiles.map((p) => [p.ID, p]));
  const scanClients: NewScanClient[] = rows.map((r) => ({
    id: r.id,
    name: r.name,
    initials: r.initials,
    email: profileById.get(r.id)?.email || undefined,
    daysSinceScan: r.daysSinceScan,
  }));
  const newSessionPath = async (id: string) => {
    const client = profileById.get(id);
    if (!client) throw new Error(`That ${noun.client} is not on your list any more.`);
    return sessionPath(id, await initSession(client, auth));
  };

  return (
    <AppShell
      entries={COACH_NAV}
      active="overview"
      onSelect={(k) => k === 'clients' && history.push('/clients')}
      onHome={() => history.push('/overview')}
      hidePhoneTabs
      phoneTitle="Overview"
      identity={
        <NavIdentity
          name={user?.name ?? 'Coach'}
          detail={profilesLoaded ? `${profiles.length} ${noun.clients}` : 'Coach'}
          imageUrl={user?.picture}
        />
      }
    >
      <div className="mx-auto flex max-w-[1296px] flex-col gap-3.5 p-4 lg:p-7">
        <PageHeader
          testId="overview-header"
          eyebrow={today}
          title={greeting(new Date(), user?.given_name ?? user?.name)}
          titleOnPhone
          meta={
            profilesLoaded
              ? waitingLine(
                  {
                    toReview: queue.filter((r) => r.reason === 'scan-to-review').length,
                    targetsReached: kpis.goalsReached,
                    quiet: queue.filter((r) => r.reason === 'overdue').length,
                  },
                  { goals: GOAL },
                )
              : `Loading your ${noun.clients}…`
          }
          actions={
            <>
              <ActionButton className="max-sm:min-h-11 max-sm:flex-1" onClick={() => history.push('/create-client')}>
                Invite a {noun.client}
              </ActionButton>
              <ActionButton
                className="max-sm:min-h-11 max-sm:flex-1"
                variant="primary"
                testId="overview-new-scan"
                onClick={() => setScanSheet({ open: true })}
              >
                New scan
              </ActionButton>
            </>
          }
        />

        {loadError ? (
          <LoadErrorCard
            testId="overview-error"
            title={`Could not load your ${noun.clients}`}
            message={loadError.message}
            detail={loadError.detail}
            onRetry={retry}
          />
        ) : (
          <>
            {/* Two to a row from the narrowest phone up. One to a row put the
                list a full screen below the date. */}
            <div
              className={`grid grid-cols-2 gap-2.5 sm:gap-3.5 ${
                GOAL ? 'xl:grid-cols-4' : '[&>*:last-child]:col-span-2 xl:grid-cols-3 xl:[&>*:last-child]:col-span-1'
              }`}
            >
              <KpiTile
                testId="kpi-active"
                label={`Active ${noun.clients}`}
                value={profilesLoaded ? kpis.activeClients : '—'}
                caption={
                  !profilesLoaded
                    ? 'loading'
                    : kpis.newThisMonth
                      ? `+${kpis.newThisMonth} this month`
                      : 'no new this month'
                }
                onClick={() => pick('active')}
                pressed={view === 'active'}
              />
              <KpiTile
                testId="kpi-attention"
                label="Needs attention"
                value={profilesLoaded ? kpis.needsAttention : '—'}
                caption={profilesLoaded ? 'prioritised below' : 'loading'}
                accent={profilesLoaded && kpis.needsAttention > 0}
                onClick={() => setView('attention')}
                pressed={view === 'attention' && filtered}
              />
              <KpiTile
                testId="kpi-recent"
                label="Scanned recently"
                value={profilesLoaded ? kpis.scannedRecently : '—'}
                caption="last 7 days"
                onClick={() => pick('recent')}
                pressed={view === 'recent'}
              />
              {GOAL && (
                <KpiTile
                  testId="kpi-goals"
                  label="Targets reached"
                  value={scansLoaded ? kpis.goalsReached : '—'}
                  caption={scansLoaded ? 'currently at target' : 'loading scans'}
                  onClick={() => pick('goals')}
                  pressed={view === 'goals'}
                />
              )}
            </div>

            {/* 1.6 : 1, the reference deck's own proportion for the queue against
                the activity panel. */}
            <div className="grid items-start gap-3.5 xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
            <div className="flex flex-col gap-3.5">
            <label className="flex h-11 items-center gap-2.5 rounded-lg border border-border bg-card px-3.5 focus-within:border-primary focus-within:ring-1 focus-within:ring-primary">
              <span className="text-muted-foreground">
                <SearchIcon size={18} />
              </span>
              <input
                data-testid="overview-search"
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={`Search ${noun.clients}`}
                aria-label={`Search ${noun.clients}`}
                className="h-full min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              />
            </label>
            <CoachCard
              testId="attention-queue"
              provenance={
                filtered
                  ? undefined
                  : GOAL
                    ? `Ordered by what you can act on: a reached target, then a scan nobody has reviewed, then a ${noun.client} who has gone quiet.`
                    : `Ordered by what you can act on: a scan nobody has reviewed, then a ${noun.client} who has gone quiet.`
              }
            >
              <div className="-mb-1 flex min-h-6 items-center justify-between gap-3">
                <Eyebrow tone={filtered ? 'primary' : 'muted'} className="truncate">
                  <span data-testid="overview-list-title">
                    {profilesLoaded
                      ? viewTitle(view, items.length, query, noun.clients)
                      : 'Needs your attention'}
                  </span>
                </Eyebrow>
                {filtered && (
                  <button
                    type="button"
                    data-testid="overview-show-queue"
                    onClick={() => {
                      setView('attention');
                      setQuery('');
                    }}
                    className="-my-2 shrink-0 px-1 py-2 text-xs font-semibold text-primary hover:underline"
                  >
                    {view === 'attention' ? 'Clear' : 'Show all'}
                  </button>
                )}
              </div>
              {!profilesLoaded ? (
                <p className="text-sm text-muted-foreground">Loading {noun.clients}…</p>
              ) : items.length === 0 ? (
                <p className="text-sm text-foreground-soft">
                  {query.trim()
                    ? `No ${noun.client} matches that search.`
                    : view === 'attention'
                      ? `Nothing waiting. Every ${noun.client} has been reviewed and none have gone quiet.`
                      : view === 'recent'
                        ? `Nobody has scanned in the last week.`
                        : view === 'goals'
                          ? `Nobody is at their target yet.`
                          : `No ${noun.clients} yet.`}
                </p>
              ) : (
                <div className="flex flex-col">
                  {items.map((item, i) => (
                    <AttentionRow
                      key={item.id}
                      item={item}
                      last={i === items.length - 1}
                      onOpen={() => open(item.id)}
                      onAction={() => act(shown[i])}
                    />
                  ))}
                </div>
              )}
            </CoachCard>
            </div>

            <ActivityPanel
              title={`Scanning · last ${ACTIVITY_DAYS} days`}
              rows={[
                {
                  label: 'New scans',
                  value: kpis.scansInWindow,
                  // The summary feed carries first and latest only, so a count of
                  // scans inside the window would be wrong. Better absent.
                  available: scansLoaded && !summarised,
                },
                ...(GOAL
                  ? [{ label: 'Targets reached', value: kpis.goalsReached, available: scansLoaded }]
                  : []),
                { label: 'Follow-ups', value: 0, available: false },
                { label: 'Reminders', value: 0, available: false },
              ]}
              footer={
                <button
                  type="button"
                  onClick={() => history.push('/clients')}
                  className="mt-1 rounded-md bg-shell py-2 text-[11px] font-medium text-primary hover:underline"
                >
                  Open the {noun.client} list →
                </button>
              }
            />
            </div>

            <div className="flex items-center justify-between gap-4">
              <p className="text-[10.5px] text-muted-foreground">
                {!scansLoaded
                  ? profilesLoaded
                    ? 'Scan figures are still loading; the ordering above does not depend on them.'
                    : null
                  : BODY_FAT
                    ? 'Body fat is estimated from the tape measurements.'
                    : null}
              </p>
            </div>
          </>
        )}
      </div>
      <NewScanSheet
        open={scanSheet.open}
        startWith={scanSheet.startWith}
        onOpenChange={(o) => setScanSheet((cur) => ({ ...cur, open: o }))}
        clients={scanClients}
        noun={noun}
        onScanHere={async (id) => history.push(await newSessionPath(id))}
        onSendLink={(id) => sendLinkEmail(id, 'scan', auth)}
        onCopyLink={async (id) => `${window.location.origin}${await newSessionPath(id)}`}
        onNewClient={() => history.push('/create-client')}
      />
    </AppShell>
  );
};

export default CoachOverview;
